import React, { useEffect, useState } from "react";
import API from "../../utils/API";
import { Button, Drawer, Field, Input, Select, useToast } from "../../ui";
import { COVERAGE_FIELDS } from "./PolicyDrawer";

/**
 * Prefilled policy edit (§2.8 "detail-first editing"): the form opens with
 * every current value — update-forms-that-start-empty are banned. Only the
 * fields below are sent; attribution/commission fields are absent by design
 * (the backend's POLICY_LOCKED strips them from non-admin writes anyway,
 * and split editing gets a real participants UI in the Commissions phase).
 */

const UPDATERS = {
  auto: (id, data) => API.updateAutoPolicy(id, data),
  home: (id, data) => API.updatePropertyPolicy(id, data),
  umbrella: (id, data) => API.updateUmbrellaPolicy(id, data)
};

function toDateInput(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

function PolicyEditDrawer({ policy, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (policy) {
      const coverage = {};
      for (const [key] of COVERAGE_FIELDS[policy.lobKey] || []) {
        coverage[key] = policy[key] || "";
      }
      setForm({
        policyNumber: policy.policyNumber || "",
        carrier: policy.carrier || "",
        effectiveDate: toDateInput(policy.effectiveDate),
        expirationDate: toDateInput(policy.expirationDate),
        premium: policy.premium == null ? "" : String(policy.premium),
        policyActive: policy.policyActive !== false ? "active" : "inactive",
        ...coverage
      });
    } else {
      setForm(null);
    }
  }, [policy]);

  if (!policy || !form) {
    return null;
  }

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

  const save = async () => {
    const premium = Number(form.premium);
    if (form.premium !== "" && (Number.isNaN(premium) || premium < 0)) {
      toast.error("Premium must be a number.");
      return;
    }
    setSaving(true);
    const payload = {
      policyNumber: form.policyNumber,
      carrier: form.carrier,
      premium: form.premium === "" ? undefined : premium,
      effectiveDate: form.effectiveDate || undefined,
      expirationDate: form.expirationDate || undefined,
      policyActive: form.policyActive === "active"
    };
    for (const [key] of COVERAGE_FIELDS[policy.lobKey] || []) {
      payload[key] = form[key];
    }
    try {
      await UPDATERS[policy.lobKey](policy._id, payload);
      toast.success("Policy saved.");
      onSaved();
    } catch (err) {
      toast.error("The policy couldn't be saved — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={`Edit ${policy.lob} policy`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} loading={saving}>
            Save changes
          </Button>
        </>
      }
    >
      <Field label="Policy number" htmlFor="pe-number">
        <Input id="pe-number" value={form.policyNumber} onChange={set("policyNumber")} />
      </Field>
      <Field label="Carrier" htmlFor="pe-carrier">
        <Input id="pe-carrier" value={form.carrier} onChange={set("carrier")} />
      </Field>
      <Field label="Status" htmlFor="pe-status">
        <Select id="pe-status" value={form.policyActive} onChange={set("policyActive")}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </Select>
      </Field>
      <Field label="Effective date" htmlFor="pe-effective">
        <Input
          id="pe-effective"
          type="date"
          value={form.effectiveDate}
          onChange={set("effectiveDate")}
        />
      </Field>
      <Field
        label="Expiration / renewal date"
        htmlFor="pe-expiration"
        help="Drives the renewal feeds on Today and the Book."
      >
        <Input
          id="pe-expiration"
          type="date"
          value={form.expirationDate}
          onChange={set("expirationDate")}
        />
      </Field>
      <Field label="Annual premium ($)" htmlFor="pe-premium">
        <Input id="pe-premium" inputMode="decimal" value={form.premium} onChange={set("premium")} />
      </Field>

      {(COVERAGE_FIELDS[policy.lobKey] || []).length > 0 ? (
        <div className="feat-kv-section">
          <h3 className="feat-kv-section__title">Coverages</h3>
          {(COVERAGE_FIELDS[policy.lobKey] || []).map(([key, label]) => (
            <Field key={key} label={label} htmlFor={`pe-${key}`}>
              <Input id={`pe-${key}`} value={form[key]} onChange={set(key)} />
            </Field>
          ))}
        </div>
      ) : null}
    </Drawer>
  );
}

export default PolicyEditDrawer;
